import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Collapse, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Input, Row, Spinner, Table } from 'reactstrap';
import AdminLayout from '../../Components/Admin/AdminLayout';
const API_BASE = process.env.REACT_APP_API_URL || 'https://api.heresmyinfo.com/api';

const MessagesMonitor = () => {
  const {
    t
  } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [groupedMessages, setGroupedMessages] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [deleting, setDeleting] = useState({});
  const [updating, setUpdating] = useState({});
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [messageTypes] = useState(['invite', 'accept', 'decline', 'email', 'sms', 'push', 'system', 'notification']);
  const [messageCategories] = useState(['invitation', 'communication', 'notification', 'system', 'verification', 'security', 'other']);
  const [selectedCategory, setSelectedCategory] = useState('');
  const groupMessagesByInvitation = useCallback(() => {
    const filtered = messages.filter(message => {
      // Search filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch = message.fromEmail?.toLowerCase().includes(searchLower) || message.toEmail?.toLowerCase().includes(searchLower) || message.subject?.toLowerCase().includes(searchLower) || message.message?.toLowerCase().includes(searchLower) || message.kind?.toLowerCase().includes(searchLower) || message.type?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      } // Category filter


      if (selectedCategory && message.type !== selectedCategory) {
        return false;
      }

      return true;
    });
    const grouped = {};
    filtered.forEach(message => {
      // Group by invitation ID if available, otherwise by connection ID, otherwise by from/to email combination
      const groupKey = message.invitationId || message.connectionId || `${message.fromEmail}-${message.toEmail}`;

      if (!grouped[groupKey]) {
        grouped[groupKey] = {
          invitationId: message.invitationId,
          connectionId: message.connectionId,
          fromEmail: message.fromEmail,
          toEmail: message.toEmail,
          fromName: message.fromName,
          toName: message.toName,
          messages: [],
          latestMessage: message.createdAt,
          recipientStatus: 'pending',
          connectionStatus: message.connection?.status || 'pending'
        };
      }

      grouped[groupKey].messages.push(message); // Update latest message time

      if (new Date(message.createdAt) > new Date(grouped[groupKey].latestMessage)) {
        grouped[groupKey].latestMessage = message.createdAt;
      }
    }); // Determine recipient and connection status for each group

    Object.values(grouped).forEach(group => {
      const kinds = new Set(group.messages.map(m => m.kind));

      if (kinds.has('decline') || kinds.has('reject')) {
        group.recipientStatus = 'rejected';
      } else if (kinds.has('accept')) {
        group.recipientStatus = 'accepted';
      } else {
        group.recipientStatus = 'pending';
      }

      group.connectionStatus = group.recipientStatus === 'accepted' ? 'connected' : group.recipientStatus === 'rejected' ? 'rejected' : 'pending';
    }); // Sort groups by latest message time

    const sortedGroups = Object.keys(grouped).sort((a, b) => new Date(grouped[b].latestMessage) - new Date(grouped[a].latestMessage));
    const sortedGrouped = {};
    sortedGroups.forEach(key => {
      // Sort messages within each group by creation time
      grouped[key].messages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      sortedGrouped[key] = grouped[key];
    });
    setGroupedMessages(sortedGrouped); // Default-open the first group if none is open

    const keys = Object.keys(sortedGrouped);

    if (keys.length > 0) {
      setExpandedGroups(prev => {
        // If any is already open, keep existing state
        if (Object.values(prev || {}).some(Boolean)) return prev;
        return {
          [keys[0]]: true
        };
      });
    }
  }, [messages, searchQuery, selectedCategory]);
  useEffect(() => {
    fetchMessages();
  }, []);
  useEffect(() => {
    groupMessagesByInvitation();
  }, [groupMessagesByInvitation]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('HMI_AUTH_TOKEN');
      const response = await fetch(`${API_BASE}/admin/messages`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        // Map the database response to match expected format
        const formattedMessages = (data.messages || []).map(msg => ({
          id: msg.id,
          kind: msg.kind,
          type: msg.type || 'other',
          subject: msg.subject,
          message: msg.message,
          data: msg.data,
          read: msg.read,
          pushed: msg.pushed,
          emailed: msg.emailed,
          sent: msg.sent,
          fromName: msg.from_first_name && msg.from_last_name ? `${msg.from_first_name} ${msg.from_last_name}` : msg.from_first_name || msg.from_email,
          toName: msg.to_first_name && msg.to_last_name ? `${msg.to_first_name} ${msg.to_last_name}` : msg.to_first_name || msg.to_email,
          fromEmail: msg.from_email,
          toEmail: msg.to_email,
          fromPhone: msg.fromPhone,
          toPhone: msg.toPhone,
          createdAt: msg.createdAt,
          updatedAt: msg.updatedAt,
          connectionId: msg.connectionId,
          connection: msg.connection_status ? {
            status: msg.connection_status
          } : null
        }));
        setMessages(formattedMessages);
      } else {
        throw new Error(data.error?.message || 'Failed to fetch messages');
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleGroup = groupKey => {
    setExpandedGroups(prev => {
      const isOpen = !!prev[groupKey];
      return isOpen ? {} : {
        [groupKey]: true
      };
    });
  };

  const deleteMessage = async messageId => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }

    try {
      setDeleting(prev => ({ ...prev,
        [messageId]: true
      }));
      const token = localStorage.getItem('HMI_AUTH_TOKEN');
      const response = await fetch(`${API_BASE}/admin/messages/${messageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        // Remove the message from the local state
        setMessages(prev => prev.filter(msg => msg.id !== messageId));
        alert('Message deleted successfully');
      } else {
        throw new Error(data.error?.message || 'Failed to delete message');
      }
    } catch (err) {
      console.error('Error deleting message:', err);
      alert('Failed to delete message: ' + err.message);
    } finally {
      setDeleting(prev => ({ ...prev,
        [messageId]: false
      }));
    }
  };

  const deleteMessageGroup = async connectionId => {
    if (!window.confirm('Are you sure you want to delete all messages in this conversation? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(prev => ({ ...prev,
        [`group-${connectionId}`]: true
      }));
      const token = localStorage.getItem('HMI_AUTH_TOKEN');
      const response = await fetch(`${API_BASE}/admin/messages/group/${connectionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        // Remove all messages with this connectionId from the local state
        setMessages(prev => prev.filter(msg => msg.connectionId !== connectionId));
        alert(data.message);
      } else {
        throw new Error(data.error?.message || 'Failed to delete message group');
      }
    } catch (err) {
      console.error('Error deleting message group:', err);
      alert('Failed to delete message group: ' + err.message);
    } finally {
      setDeleting(prev => ({ ...prev,
        [`group-${connectionId}`]: false
      }));
    }
  };

  const updateMessageType = async (messageId, newKind) => {
    try {
      setUpdating(prev => ({ ...prev,
        [messageId]: true
      }));
      const token = localStorage.getItem('HMI_AUTH_TOKEN');
      const response = await fetch(`${API_BASE}/admin/messages/${messageId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          kind: newKind
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        // Update the message in the local state
        setMessages(prev => prev.map(msg => msg.id === messageId ? { ...msg,
          kind: newKind,
          updatedAt: data.message.updatedAt
        } : msg));
        alert('Message type updated successfully');
      } else {
        throw new Error(data.error?.message || 'Failed to update message type');
      }
    } catch (err) {
      console.error('Error updating message type:', err);
      alert('Failed to update message type: ' + err.message);
    } finally {
      setUpdating(prev => ({ ...prev,
        [messageId]: false
      }));
    }
  };

  const getKindBadgeColor = kind => {
    const colors = {
      'invite': 'primary',
      'accept': 'success',
      'decline': 'danger',
      'email': 'info',
      'sms': 'warning',
      'push': 'secondary'
    };
    return colors[kind] || 'secondary';
  };

  const getTypeBadgeColor = type => {
    const colors = {
      'invitation': 'primary',
      'communication': 'info',
      'notification': 'warning',
      'system': 'secondary',
      'verification': 'success',
      'security': 'danger',
      'other': 'light'
    };
    return colors[type] || 'light';
  };

  const getStatusBadgeColor = status => {
    const colors = {
      'pending': 'warning',
      'connected': 'success',
      'rejected': 'danger',
      'expired': 'secondary'
    };
    return colors[status] || 'secondary';
  };

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
  };

  const formatRelativeTime = dateString => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  if (loading) {
    return <AdminLayout>
                <div style={{
        padding: 20,
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
                    <div className="text-center">
                        <Spinner color="primary" />
                        <p className="mt-2">{t("Loading messages...")}</p>
                    </div>
                </div>
            </AdminLayout>;
  }

  if (error) {
    return <AdminLayout>
                <div style={{
        padding: 20,
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
                    <Alert color="danger">
                        <h4>{t("Error")}</h4>
                        <p>{error}</p>
                        <Button color="primary" onClick={fetchMessages}>{t("Retry")}</Button>
                    </Alert>
                </div>
            </AdminLayout>;
  }

  return <AdminLayout>
            <div style={{
      padding: 20,
      maxWidth: '1400px',
      margin: '0 auto'
    }}>
                <h4 className="mb-4" style={{
        fontSize: '1.5rem',
        textTransform: 'none',
        letterSpacing: 'normal',
        fontWeight: 600,
        color: 'white'
      }}>{t("Message Management")}</h4>

                {
        /* Search and Filters */
      }
                <div className="mb-4">
                    <Row>
                        <Col md={6}>
                            <Input type="text" placeholder={t("Search messages by email, subject, or content...")} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{
              maxWidth: '400px'
            }} />
                        </Col>
                        <Col md={6}>
                            <Dropdown isOpen={openDropdowns.categoryFilter} toggle={() => setOpenDropdowns(prev => ({ ...prev,
              categoryFilter: !prev.categoryFilter
            }))}>
                                <DropdownToggle caret>
                                    {selectedCategory ? `Category: ${selectedCategory}` : 'All Categories'}
                                </DropdownToggle>
                                <DropdownMenu>
                                    <DropdownItem onClick={() => setSelectedCategory('')}>{t("All Categories")}</DropdownItem>
                                    {messageCategories.map(category => <DropdownItem key={category} onClick={() => setSelectedCategory(category)}>
                                            {category.charAt(0).toUpperCase() + category.slice(1)}
                                        </DropdownItem>)}
                                </DropdownMenu>
                            </Dropdown>
                        </Col>
                    </Row>
                </div>

                {
        /* Summary Stats */
      }
                <Row className="mb-4">
                    <Col md="2">
                        <Card className="text-center p-3">
                            <h5>{Object.keys(groupedMessages).length}</h5>
                            <small className="text-muted">{t("Conversations")}</small>
                        </Card>
                    </Col>
                    <Col md="2">
                        <Card className="text-center p-3">
                            <h5>{messages.length}</h5>
                            <small className="text-muted">{t("Total Messages")}</small>
                        </Card>
                    </Col>
                    <Col md="2">
                        <Card className="text-center p-3">
                            <h5>{messages.filter(m => !m.read).length}</h5>
                            <small className="text-muted">{t("Unread")}</small>
                        </Card>
                    </Col>
                    <Col md="2">
                        <Card className="text-center p-3">
                            <h5>{messages.filter(m => m.type === 'invitation').length}</h5>
                            <small className="text-muted">{t("Invitations")}</small>
                        </Card>
                    </Col>
                    <Col md="2">
                        <Card className="text-center p-3">
                            <h5>{messages.filter(m => m.type === 'communication').length}</h5>
                            <small className="text-muted">{t("Communications")}</small>
                        </Card>
                    </Col>
                    <Col md="2">
                        <Card className="text-center p-3">
                            <h5>{messages.filter(m => m.type === 'system').length}</h5>
                            <small className="text-muted">{t("System")}</small>
                        </Card>
                    </Col>
                </Row>

                {
        /* Messages grouped by invitation */
      }
                <div>
                    {Object.keys(groupedMessages).length === 0 ? <Card className="text-center p-4">
                            <p className="text-muted">{t("No messages found.")}</p>
                        </Card> : Object.entries(groupedMessages).map(([groupKey, group]) => <Card key={groupKey} className="mb-3">
                                <div className="card-header  cursor-pointer" onClick={() => toggleGroup(groupKey)} style={{
            cursor: 'pointer'
          }}>
                                    <Row className="align-items-center">
                                        <Col md="6">
                                            <div>
                                                <strong>
                                                    {group.fromName || group.fromEmail} → {group.toName || group.toEmail}
                                                </strong>
                                                {group.invitationId && <Badge color="primary" className="ml-2">{t("Invitation:")}{group.invitationId.substring(0, 15)}...
                                                    </Badge>}
                                                {group.connectionId && !group.invitationId && <Badge color="info" className="ml-2">{t("Connection:")}{group.connectionId.substring(0, 8)}...
                                                    </Badge>}
                                            </div>
                                            <small className="text-muted">
                                                {group.messages.length}{t("message")}{group.messages.length !== 1 ? 's' : ''}{t("\u2022 Last:")}{formatRelativeTime(group.latestMessage)}
                                            </small>
                                        </Col>
                                        <Col md="3">
                                            <Badge color={getStatusBadgeColor(group.connectionStatus)}>
                                                {group.connectionStatus}
                                            </Badge>
                                        </Col>
                                        <Col md="3" className="text-right">
                                            <Button color="danger" size="sm" className="mr-2" onClick={e => {
                  e.stopPropagation();
                  deleteMessageGroup(group.connectionId);
                }} disabled={deleting[`group-${group.connectionId}`]}>
                                                {deleting[`group-${group.connectionId}`] ? <>
                                                        <Spinner size="sm" className="mr-1" />{t("Deleting...")}</> : '🗑️ Delete Group'}
                                            </Button>
                                            <Badge color="secondary">
                                                {expandedGroups[groupKey] ? '▼' : '▶'}
                                            </Badge>
                                        </Col>
                                    </Row>
                                </div>

                                <Collapse isOpen={expandedGroups[groupKey]}>
                                    <div className="card-body">
                                        <Table responsive size="sm">
                                            <thead>
                                                <tr>
                                                    <th>{t("Type")}</th>
                                                    <th>{t("From")}</th>
                                                    <th>{t("To")}</th>
                                                    <th>{t("Subject")}</th>
                                                    <th>{t("Message")}</th>
                                                    <th>{t("Time")}</th>
                                                    <th>{t("Recipient Status")}</th>
                                                    <th>{t("Connection Status")}</th>
                                                    <th>{t("Actions")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {group.messages.map(message => <tr key={message.id}>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <Badge color={getTypeBadgeColor(message.type)} className="mr-2">
                                                                    {message.type || 'other'}
                                                                </Badge>
                                                                <Dropdown isOpen={openDropdowns[message.id] || false} toggle={() => setOpenDropdowns(prev => ({ ...prev,
                          [message.id]: !prev[message.id]
                        }))} disabled={updating[message.id]}>
                                                                    <DropdownToggle caret color={getKindBadgeColor(message.kind)} size="sm" disabled={updating[message.id]}>
                                                                        {updating[message.id] ? <>
                                                                                <Spinner size="sm" className="mr-1" />{t("Updating...")}</> : message.kind}
                                                                    </DropdownToggle>
                                                                    <DropdownMenu>
                                                                        {messageTypes.map(type => <DropdownItem key={type} onClick={() => {
                              updateMessageType(message.id, type);
                              setOpenDropdowns(prev => ({ ...prev,
                                [message.id]: false
                              }));
                            }} active={message.kind === type}>
                                                                                <Badge color={getKindBadgeColor(type)} className="mr-2">
                                                                                    {type}
                                                                                </Badge>
                                                                                {type}
                                                                            </DropdownItem>)}
                                                                    </DropdownMenu>
                                                                </Dropdown>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                <strong>{message.fromName || 'Unknown'}</strong>
                                                                <br />
                                                                <small className="text-muted">{message.fromEmail}</small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                <strong>{message.toName || 'Unknown'}</strong>
                                                                <br />
                                                                <small className="text-muted">{message.toEmail}</small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div style={{
                        maxWidth: '200px'
                      }}>
                                                                {message.subject || '-'}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div style={{
                        maxWidth: '300px',
                        wordWrap: 'break-word'
                      }}>
                                                                {message.message.length > 100 ? `${message.message.substring(0, 100)}...` : message.message}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <small>{formatDate(message.createdAt)}</small>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                {message.read && <Badge color="success" size="sm">{t("Read")}</Badge>}
                                                                {message.emailed && <Badge color="info" size="sm">{t("Emailed")}</Badge>}
                                                                {message.pushed && <Badge color="warning" size="sm">{t("Pushed")}</Badge>}
                                                                {message.sent > 0 && <Badge color="primary" size="sm">{t("Sent:")}{message.sent}</Badge>}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <Button color="danger" size="sm" onClick={() => deleteMessage(message.id)} disabled={deleting[message.id]}>
                                                                {deleting[message.id] ? <>
                                                                        <Spinner size="sm" className="mr-1" />{t("Deleting...")}</> : '🗑️'}
                                                            </Button>
                                                        </td>
                                                    </tr>)}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Collapse>
                            </Card>)}
                </div>
            </div>
        </AdminLayout>;
};

export default MessagesMonitor;